/**
 * Company labels
 * Dynamic mapping from JSON based on current language
 */

import svCompany from '../locales/sv/company.json';
import enCompany from '../locales/en/company.json';
import { getLanguage, type Language } from '@/utils/languageUtils';

/**
 * Helper function to get the correct locale based on language
 */
const getCompanyLocale = (lang?: Language) => {
  const currentLang = lang || getLanguage();
  return currentLang === 'en' ? enCompany : svCompany;
};

/**
 * Company Status labels - dynamically uses current language
 */
export const companyStatusLabels = (lang?: Language): Record<string, string> => {
  const locale = getCompanyLocale(lang);
  return {
    NEW_CONTACT: locale.status.newContact,
    CONTACTED: locale.status.contacted,
    ACTIVE_DIALOG: locale.status.activeDialog,
    COME_BACK_LATER: locale.status.comeBackLater,
    CONNECTED: locale.status.connected,
    NOT_INTERESTED: locale.status.notInterested,
  };
};

/** Reverse lookup: Label -> CompanyStatus */
export const labelToCompanyStatus = (lang?: Language): Record<string, string> => {
  const labels = companyStatusLabels(lang);
  return Object.fromEntries(
    Object.entries(labels).map(([enumKey, label]) => [label, enumKey]),
  ) as Record<string, string>;
};

/**
 * Company UI labels - dynamically uses current language
 */
export const companyUILabels = (lang?: Language): Record<string, string> => {
  const locale = getCompanyLocale(lang);
  return locale.ui || {};
};

/**
 * Company form labels - dynamically uses current language
 */
export const companyFormLabels = (lang?: Language): Record<string, string> => {
  const locale = getCompanyLocale(lang);
  return locale.form || {};
};

/**
 * Company validation labels - dynamically uses current language
 */
export const companyValidationLabels = (lang?: Language): Record<string, string> => {
  const locale = getCompanyLocale(lang);
  return locale.validation || {};
};

/**
 * Company toast messages - dynamically uses current language
 */
export const companyToastLabels = (lang?: Language): Record<string, string> => {
  const locale = getCompanyLocale(lang);
  return locale.toast || {};
};

/**
 * Company delete labels - dynamically uses current language
 */
export const companyDeleteLabels = (lang?: Language): Record<string, string> => {
  const locale = getCompanyLocale(lang);
  return locale.delete || {};
};


